import React, { MouseEvent, useMemo } from "react";
import styles from "./sectionContent.module.scss";
import { ContentType } from "../../../../types";
import IconPDF from "@src/assets/icons/content_type_pdf.svg?react";
import IconAudio from "@src/assets/icons/content_type_audio.svg?react";
import IconSlide from "@src/assets/icons/content_type_slide.svg?react";
import IconVideo from "@src/assets/icons/content_type_video.svg?react";
import IconForm from "@src/assets/icons/content_type_form.svg?react";
import {
  LastVisitedLessonContentFragment as LastVisitedLesson,
  SllModule,
  SllLesson,
  SllSectionFragment,
} from "@api/graphql-codegen";
import { useStandaloneContent } from "@src/hooks/useStandaloneContent";
import Checkbox from "@src/components/checkbox/Checkbox";

type Props = {
  content: SllModule;
  lesson?: SllLesson | SllSectionFragment;
  lastVisitedLesson?: LastVisitedLesson;
  selected?: boolean;
  disabled?: boolean;
  closeNav?: () => void;
};

const getContentIcon = (type: string) => {
  switch (type) {
    case ContentType.PDF: {
      return IconPDF;
    }
    case ContentType.AUDIO: {
      return IconAudio;
    }
    // case ContentType.GOOGLESLIDE:
    case ContentType.SLIDE: {
      return IconSlide;
    }
    case ContentType.REVEALSLIDE:
    case ContentType.ZIPSLIDE: {
      return IconSlide;
    }
    case ContentType.VIDEO: {
      return IconVideo;
    }
    case ContentType.FORM: {
      return IconForm;
    }
    default: {
      return null;
    }
  }
};

function SectionContent(props: Props) {
  const { content, selected, lesson, disabled, closeNav } = props;
  const { name } = content;
  const {
    isNewLessonLayout,
    setContent,
    setLesson,
    setSection,
    setSelectionLevel,
    content: selectedContent,
  } = useStandaloneContent();

  const Icon = getContentIcon(content.type);

  const handleSelect = (e: MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    setContent(content);
    if (lesson?.__typename === "SLLLesson") {
      setLesson(lesson);
      setSection(undefined);
    } else if (lesson?.__typename === "SLLSection") {
      setSection(lesson);
    }
    setSelectionLevel("Content");
    closeNav && closeNav();
  };

  const handleCheck = (e: any) => {
    if (lesson?.__typename === "SLLLesson") {
      setLesson(lesson);
      setSection(undefined);
    } else if (lesson?.__typename === "SLLSection") {
      setSection(lesson);
    }
    setSelectionLevel("Content");
    closeNav && closeNav();

    if (content === selectedContent) {
      const curIndex =
        (lesson as SllLesson)?.presentation?.findIndex(
          (m) => m?.id === content.id
        ) || 0;
      const prevModule =
        curIndex >= 1
          ? (lesson as SllLesson)?.presentation?.at(curIndex - 1)
          : undefined;
      if (prevModule) {
        setContent(prevModule);
      }
    } else {
      setContent(content);
    }
  };

  const wrapperClassName = useMemo(() => {
    const classes = [isNewLessonLayout ? styles.checkwrapper : styles.wrapper];
    if (selected && selectedContent === content) {
      classes.push(styles.selected);
    }
    if (disabled) {
      classes.push(styles.disabled);
    }
    return classes.join(" ");
  }, [disabled, selected, isNewLessonLayout, selectedContent, content]);

  if (isNewLessonLayout) {
    return (
      <div className={wrapperClassName}>
        <Checkbox
          id={`check_${name}`}
          label={name}
          checked={selected ?? false}
          className={styles.itemView}
          onChange={handleCheck}
        />
        <div className={styles.meta}></div>
        <div className={styles.typeIconWrapper}>
          {content.type === ContentType.PARENT ? (
            <div className={styles.paColor}>PS</div>
          ) : (
            Icon && <Icon className={styles.typeIcon} />
          )}
        </div>
      </div>
    );
  }

  return (
    <div
      className={wrapperClassName}
      onClick={disabled ? () => {} : handleSelect}
    >
      <div className={styles.circle} />
      <div className={styles.title}>{name}</div>
      <div className={styles.meta}></div>
      <div className={styles.typeIconWrapper}>
        {content.type === ContentType.PARENT ? (
          <div className={styles.paColor}>PS</div>
        ) : (
          Icon && <Icon className={styles.typeIcon} />
        )}
      </div>
    </div>
  );
}

export default React.memo(SectionContent);
